import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Typography,
} from '@mui/material';
import { callBackend } from '../utils/api';
import { useSecurityOperationContext } from '../context/SecurityOperationContext';
import {
    isSecurityOperationEventForScope, isSuccessfulWatchlistRevisionPublication,
    WATCHLIST_REVISION_PROGRESS_EVENT,
} from '../utils/securityResolutionEvents';
import { parseCloudRunError } from './msiDriftReviewHelpers';
import { normalizeWatchlistLifecycle } from '../utils/watchlistLifecycle';
import WatchlistUploader from './watchlist_table/WatchlistUploader';
import useWatchlistReviewIdentity from './watchlist_table/useWatchlistReviewIdentity';
import { CountryFlagText } from './shared/securityDisplayPrimitives';

function matchingExplanation(row) {
    if (row.reason === 'not_in_watchlist') return 'Not in Watchlist';
    if (String(row.reason || '').includes('ambiguous')) return 'Identity needs review';
    return 'Identity could not be matched';
}

// Retain the confirmed portfolio input while its missing securities pass through
// the ordinary Watchlist review and publication. Actual is reviewed separately.
export default function PortfolioImportWatchlistReview({ review, onContinue, onCancel }) {
    const { sessionScopeToken } = useSecurityOperationContext();
    const identity = useWatchlistReviewIdentity();
    const [staging, setStaging] = useState(false);
    const [error, setError] = useState('');
    const [revisionId, setRevisionId] = useState(null);
    const [equivalentPending, setEquivalentPending] = useState(false);
    const [commandId, setCommandId] = useState(1);
    const [showChoices, setShowChoices] = useState(true);
    const attemptIdRef = useRef(null);
    const resumedRef = useRef(false);
    const mountedRef = useRef(true);
    const reviewSessionRef = useRef(sessionScopeToken);
    const currentSessionRef = useRef(sessionScopeToken);
    currentSessionRef.current = sessionScopeToken;
    const continueRef = useRef(onContinue);
    continueRef.current = onContinue;
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const isCurrentReview = () => mountedRef.current
        && currentSessionRef.current === reviewSessionRef.current;
    const resumeImport = useCallback((request = review.request) => {
        if (!mountedRef.current || currentSessionRef.current !== reviewSessionRef.current
            || resumedRef.current) return;
        resumedRef.current = true;
        continueRef.current(request);
    }, [review.request]);

    const handoff = useMemo(() => revisionId ? {
        commandId, revisionId, equivalentPending, sessionScopeToken, origin: 'portfolio_import',
        status: 'changes_awaiting_review',
    } : null, [commandId, revisionId, equivalentPending, sessionScopeToken]);

    useEffect(() => {
        if (!revisionId || sessionScopeToken !== reviewSessionRef.current) return undefined;
        const completed = (event) => {
            const detail = event.detail;
            if (resumedRef.current || currentSessionRef.current !== reviewSessionRef.current
                || !isSecurityOperationEventForScope(detail, sessionScopeToken)
                || detail?.watchlist_revision_id !== revisionId) return;
            try {
                if (['failed', 'cancelled', 'stale', 'superseded'].includes(normalizeWatchlistLifecycle(detail).status)) {
                    setError('The Watchlist update did not complete. Your portfolio has not changed. Review the Watchlist status before importing again.');
                    return;
                }
            } catch (_error) { return; }
            if (!isSuccessfulWatchlistRevisionPublication(detail)) return;
            // Re-resolve against the newly published Watchlist. Remaining misses
            // require another explicit choice; never add or apply in a loop.
            resumeImport();
        };
        window.addEventListener(WATCHLIST_REVISION_PROGRESS_EVENT, completed);
        return () => window.removeEventListener(WATCHLIST_REVISION_PROGRESS_EVENT, completed);
    }, [resumeImport, revisionId, sessionScopeToken]);

    const stageAdditions = async () => {
        if (staging || resumedRef.current || !isCurrentReview()) return;
        setStaging(true);
        setError('');
        try {
            if (!attemptIdRef.current) attemptIdRef.current = crypto.randomUUID();
            const response = await callBackend('api/portfolio/imports/watchlist-additions/stage', 'POST', {
                ...review.request,
                expected_watchlist_revision_id: review.response.current_watchlist_revision_id,
                attempt_id: attemptIdRef.current,
            });
            if (!isCurrentReview()) return;
            if (response?.success && response.status === 'not_needed') {
                resumeImport();
                return;
            }
            const stagedId = response?.watchlist_revision_id || response?.pending_revision?.id;
            if (!response?.success || !stagedId) {
                throw new Error(response?.message || 'The Watchlist change could not be prepared.');
            }
            setRevisionId(stagedId);
            setEquivalentPending(response.equivalent_pending === true);
            setShowChoices(false);
        } catch (stageError) {
            if (isCurrentReview()) setError(parseCloudRunError(stageError).message);
        } finally {
            if (isCurrentReview()) setStaging(false);
        }
    };
    const missing = review.response.missing_watchlist || [];
    const matchedCount = Number(review.response.matched_position_count) || 0;
    const needsExternalIdentifier = missing.some((row) => String(row.security_id_type || '').toLowerCase() === 'cognive_id');
    if (sessionScopeToken !== reviewSessionRef.current) return null;
    return <>
        <Dialog open={showChoices} onClose={() => { if (!staging) onCancel(); }} maxWidth="md" fullWidth
            aria-labelledby="portfolio-watchlist-review-title">
            <DialogTitle id="portfolio-watchlist-review-title">Portfolio securities need Watchlist review</DialogTitle>
            <DialogContent dividers>
                <Typography sx={{ mb: 1.5 }}>
                    {missing.length} uploaded {missing.length === 1 ? 'security is' : 'securities are'} not matched to your Watchlist.
                    Add them to the Watchlist first, then review the portfolio import. Your Actual holdings have not changed.
                </Typography>
                {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
                <TableContainer sx={{ maxHeight: 360 }}>
                    <Table size="small" stickyHeader aria-label="Portfolio securities requiring Watchlist review">
                        <TableHead><TableRow>
                            <TableCell>{identity.header}</TableCell><TableCell>Country</TableCell><TableCell>Status</TableCell>
                        </TableRow></TableHead>
                        <TableBody>{missing.map((row, index) => <TableRow key={index}>
                            <TableCell>{identity.renderIdentity(row)}</TableCell>
                            <TableCell>{row.country ? <CountryFlagText country={row.country} /> : '—'}</TableCell>
                            <TableCell>{matchingExplanation(row)}</TableCell>
                        </TableRow>)}</TableBody>
                    </Table>
                </TableContainer>
                {identity.selector}
                {needsExternalIdentifier && <Alert severity="info" sx={{ mt: 1.5 }}>
                    To add these securities to your Watchlist, upload their ticker, SEDOL, or another supported external identifier.
                </Alert>}
                {!matchedCount && <Alert severity="info" sx={{ mt: 1.5 }}>
                    No positions can be imported yet. Your existing portfolio will be kept.
                </Alert>}
            </DialogContent>
            <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
                <Button disabled={staging} onClick={onCancel}>Cancel import</Button>
                {matchedCount > 0 && <Button disabled={staging} onClick={() => resumeImport({
                    ...review.request, missing_watchlist_action: 'exclude',
                })}>Import matched securities only ({matchedCount})</Button>}
                <Button variant="contained" disabled={staging || needsExternalIdentifier} onClick={stageAdditions}
                    startIcon={staging ? <CircularProgress size={16} /> : undefined}>
                    Add missing to Watchlist
                </Button>
            </DialogActions>
        </Dialog>
        {revisionId && <>
            <Box sx={{ mt: 1 }}>
                <Alert severity={error ? 'warning' : 'info'} action={<Button onClick={() => setCommandId((value) => value + 1)}>
                    Review Watchlist change
                </Button>}>
                    {error || 'Portfolio import is waiting for the Watchlist update. Once it completes, the portfolio review will open.'}
                </Alert>
                <Button size="small" onClick={onCancel}>Stop waiting for portfolio import</Button>
            </Box>
            <WatchlistUploader reviewOnly externalReviewHandoff={handoff}
                onReviewRevisionChanged={setRevisionId}
                onReviewAlreadyCurrent={() => resumeImport()} onReviewIgnored={() => {
                if (!isCurrentReview() || resumedRef.current) return;
                setRevisionId(null);
                attemptIdRef.current = null;
                setError('');
                setShowChoices(true);
            }} />
        </>}
    </>;
}
